import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { dashboardAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Trophy, TrendingDown, Loader2, ChevronRight, AlertCircle } from 'lucide-react';

const PROGRESS_BG = 'bg-slate-100 dark:bg-slate-800';

export const WinLossCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(null); // 'won' | 'lost' | null

  useEffect(() => {
    dashboardAPI.getWinLoss()
      .then(res => setData(res.data))
      .catch(err => console.error('Win/loss fetch failed', err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Card className="border-slate-200" data-testid="win-loss-card-loading">
        <CardContent className="p-6 flex items-center justify-center h-44">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const total = (data.won_count || 0) + (data.lost_count || 0);
  const winPct = total ? (data.won_count / total) * 100 : 0;

  return (
    <>
      <Card className="border-slate-200" data-testid="win-loss-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-500" />
              Win / Loss — {data.month_label}
            </CardTitle>
            {total > 0 && (
              <Badge className="bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-50" data-testid="win-loss-rate">
                {data.win_rate}% win rate
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Counts row */}
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => data.won_count > 0 && setOpenDialog('won')}
              className={`text-left p-3 rounded-lg border border-emerald-100 bg-emerald-50/60 hover:bg-emerald-50 transition-colors ${data.won_count === 0 ? 'cursor-default opacity-80' : ''}`}
              data-testid="win-loss-won-block"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-emerald-700">
                  <Trophy className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium uppercase tracking-wide">Accepted</span>
                </div>
                {data.won_count > 0 && <ChevronRight className="w-3.5 h-3.5 text-emerald-500" />}
              </div>
              <p className="text-2xl font-bold text-emerald-700 mt-1" data-testid="win-loss-won-count">{data.won_count}</p>
              <p className="text-xs text-emerald-700/70 mt-0.5" data-testid="win-loss-won-value">{formatCurrency(data.won_value)}</p>
            </button>
            <button
              type="button"
              onClick={() => data.lost_count > 0 && setOpenDialog('lost')}
              className={`text-left p-3 rounded-lg border border-rose-100 bg-rose-50/60 hover:bg-rose-50 transition-colors ${data.lost_count === 0 ? 'cursor-default opacity-80' : ''}`}
              data-testid="win-loss-lost-block"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-rose-700">
                  <TrendingDown className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium uppercase tracking-wide">Declined</span>
                </div>
                {data.lost_count > 0 && <ChevronRight className="w-3.5 h-3.5 text-rose-500" />}
              </div>
              <p className="text-2xl font-bold text-rose-700 mt-1" data-testid="win-loss-lost-count">{data.lost_count}</p>
              <p className="text-xs text-rose-700/70 mt-0.5" data-testid="win-loss-lost-value">{formatCurrency(data.lost_value)}</p>
            </button>
          </div>

          {/* Win rate bar */}
          {total > 0 && (
            <div>
              <div className={`relative h-2 w-full rounded-full overflow-hidden ${PROGRESS_BG}`} data-testid="win-loss-bar">
                <div
                  className="absolute inset-y-0 left-0 bg-emerald-500"
                  style={{ width: `${winPct}%` }}
                />
                <div
                  className="absolute inset-y-0 right-0 bg-rose-400"
                  style={{ width: `${100 - winPct}%` }}
                />
              </div>
              <div className="flex justify-between text-[10px] text-slate-500 mt-1.5">
                <span>{Math.round(winPct)}% won</span>
                <span>{Math.round(100 - winPct)}% lost</span>
              </div>
            </div>
          )}

          {/* Top decline reasons */}
          <div>
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <AlertCircle className="w-3 h-3 text-slate-400" />
              Top Decline Reasons
            </p>
            {(data.top_decline_reasons || []).length === 0 ? (
              <p className="text-xs text-slate-400" data-testid="win-loss-no-reasons">No declines this month — nice!</p>
            ) : (
              <ul className="space-y-1.5" data-testid="win-loss-top-reasons">
                {data.top_decline_reasons.map((r, idx) => (
                  <li key={r.reason} className="flex items-center justify-between text-xs">
                    <span className="text-slate-600 truncate pr-2">
                      <span className="text-slate-400 mr-1.5">#{idx + 1}</span>
                      {r.reason}
                    </span>
                    <Badge variant="outline" className="text-[10px] flex-shrink-0">
                      {r.count}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={openDialog !== null} onOpenChange={(o) => !o && setOpenDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="win-loss-dialog">
          <DialogHeader>
            <DialogTitle>
              {openDialog === 'won' ? `Accepted quotes — ${data.month_label}` : `Declined quotes — ${data.month_label}`}
            </DialogTitle>
            <DialogDescription>
              {openDialog === 'won'
                ? `${data.won_count} accepted · ${formatCurrency(data.won_value)} total`
                : `${data.lost_count} declined · ${formatCurrency(data.lost_value)} lost value`}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-2">
            {(openDialog === 'won' ? data.won_customers : data.lost_customers).map((c) => (
              <Link
                key={c.id}
                to={`/customers/${c.id}`}
                onClick={() => setOpenDialog(null)}
                className="block p-3 border border-slate-100 rounded-lg hover:bg-slate-50 hover:border-slate-200 transition-colors"
                data-testid={`win-loss-row-${c.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900 truncate">{c.name || 'Unnamed customer'}</p>
                    {openDialog === 'lost' && c.reason && (
                      <p className="text-xs text-rose-600 mt-0.5 truncate">{c.reason}</p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-semibold text-slate-900">{formatCurrency(c.value)}</p>
                    <ChevronRight className="w-3.5 h-3.5 text-slate-400 ml-auto" />
                  </div>
                </div>
              </Link>
            ))}
            {((openDialog === 'won' ? data.won_customers : data.lost_customers) || []).length === 0 && (
              <p className="text-sm text-slate-400 text-center py-6">No records</p>
            )}
          </div>
          <div className="flex justify-end pt-2">
            <Button variant="outline" size="sm" onClick={() => setOpenDialog(null)} data-testid="win-loss-dialog-close">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WinLossCard;
